import { useContext } from 'react';
import { ContextHolder } from '@frontegg/rest-api';
import { FronteggStoreContext, useStore } from '../FronteggStoreContext';
import { useSnapshot } from '../useSnapshot';
import { useShadowDom } from '../common';
const defaultMapper = {
  state: state => state,
  actions: actions => actions
};

/**
 * Use this `frontegg` hook function to obtain the complete authentication state, if it exists.
 *
 * ```jsx
 * export const MyFunctionComponent = () => {
 *   const { isAuthenticated, user } = useAuth();
 *
 *   return isAuthenticated ? <div>Hello, User {user.name}</div> : null;
 * }
 * ```
 *
 * You can also utilize other `frontegg` hooks like `useAuthUser` to specifically retrieve the user and redirect to the login page if necessary, `useAuthUserOrNull` to get the user if available, and `useIsAuthenticated` for checking authentication status.
 */

export function useAuth(stateMapper = defaultMapper.state) {
  const state = useStore().store.auth;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return stateMapper(useSnapshot(state));
}
export function useAuthState() {
  const state = useStore().store.auth;
  return useSnapshot(state);
}

/**
 * ```jsx
 * export const MyFunctionComponent = () => {
 *   const { isAuthenticated, user } = useAuth();
 *   const loginWithRedirect = useLoginWithRedirect();
 *
 *   if (!isAuthenticated) {
 *     loginWithRedirect();
 *     return <></>;
 *   }
 *
 *   return (<div>Hello User {user.name}</div>);
 * }
 * ```
 *
 /**
 * Use this frontegg hook function to redirect the user to the login page when in hosted login mode.
 * If the user is already authenticated, this method will direct the user to the store, and you can retrieve user information using the `useAuthUserOrNull` method.
 *
 * To ensure the user is available on the first page load when authenticated, configure this option in your `FronteggProvider`:
 * `authOptions`:
 *   `hostedLoginOptions`:
 *     `loadUserOnFirstLoad: true`
 *
 * When using this option, you can have the user on the first load, and you can control when the user is redirected to the login page by using `loginWithRedirect`.
 */
export const useLoginWithRedirect = () => {
  const {
    actions
  } = useContext(FronteggStoreContext);
  return actions.requestHostedLoginAuthorize;
};
export const useLoginWithRedirectV2 = () => {
  const {
    actions
  } = useContext(FronteggStoreContext);
  return actions.requestHostedLoginAuthorizeV2;
};
export const useAuthActions = () => {
  return useStore().actions;
};
export const useOnRedirectTo = () => {
  const {
    onRedirectTo
  } = useAuth();
  const {
    appName
  } = useShadowDom();
  return onRedirectTo || ContextHolder.for(appName).onRedirectTo;
};
export const useAuthRoutes = () => useAuthState().routes;

/**
 * ```jsx
 * export const MyFunctionComponent = () => {
 *   const isAuthenticated  = useIsAuthenticated();
 *   return isAuthenticated ? <div>Hello User</div> : <Redirect to={'/login'}/>
 * }
 * ```
 *
 * use this frontegg hook function to get if user is "Authenticated"
 */
export const useIsAuthenticated = () => {
  const {
    isAuthenticated
  } = useSnapshot(useStore().store.auth);
  return isAuthenticated;
};

/**
 * ```jsx
 * export const MyFunctionComponent = () => {
 *   const user = useAuthUser();
 *   return user ? <div>Hello {user.name}!</div> : <div>Hello Guest!</div>
 * }
 * ```
 *
 * Use this `frontegg` hook function to obtain the authenticated user. If the user is not authenticated, this method will immediately redirect the user to the login page. (Primarily intended for embedded mode usage)
 * To prevent this immediate redirection behavior, use the `useAuthUserOrNull` method.
 */
export const useAuthUser = () => {
  const {
    loginUrl,
    customLoginUrl
  } = useAuthRoutes();
  const onRedirectTo = useOnRedirectTo();
  const {
    user
  } = useAuth();
  const isSSR = typeof window === 'undefined';
  const noUser = {};
  if (user == null && !isSSR) {
    onRedirectTo(customLoginUrl != null ? customLoginUrl : loginUrl, {
      refresh: true
    });
    return noUser;
  }
  return user || noUser;
};

/**
 * ```jsx
 * export const MyFunctionComponent = () => {
 *   const user = useAuthUserOrNull();
 *   return user ? <div>Hello {user.name}!</div> : <div>Hello Guest!</div>
 * }
 * ```
 *
 * Use this `frontegg` hook function to retrieve the authenticated user. If the user is not authenticated, this hook will return null. To redirect the user to the login page in case they are not authenticated, use the `useAuthUser` method.
 */
export const useAuthUserOrNull = () => {
  const {
    user
  } = useAuth();
  return user || null;
};